import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { type ColumnDef } from '@tanstack/react-table';
import { Badge, Button, LoadingSpinner, Table, TextLink, Tooltip } from '@shared/components';
import { PermissionGuard, usePermissions } from '@/features/auth';
import { ChevronsUpDownIcon, ChevronUpIcon, ChevronDownIcon, EyeIcon, EditIcon } from 'lucide-react';
import { useDealersByIds, buildDealerNameMap } from '@/features/dealers/hooks/useDealersByIds';
import type { AdminUser } from '../types/users.types';

interface SortConfig {
  sort_by?: string;
  sort_direction?: 'asc' | 'desc';
}

interface Props {
  data: AdminUser[];
  isLoading?: boolean;
  sortConfig?: SortConfig;
  onSort?: (column: string) => void;
  emptyMessage?: string;
}

const HomeDealerCell: React.FC<{
  dealerId: string | null;
  nameMap: Map<string, string>;
  isLoading: boolean;
}> = ({ dealerId, nameMap, isLoading }) => {
  if (!dealerId) {
    return <span className="text-mp-muted">—</span>;
  }
  const name = nameMap.get(dealerId);
  if (!name) {
    return isLoading
      ? <span className="text-mp-muted">…</span>
      : <span className="font-mono text-xs" title={dealerId}>{dealerId}</span>;
  }
  return (
    <TextLink to={`/admin/dealers/${dealerId}`} color="primary">
      {name}
    </TextLink>
  );
};

function formatRelativeTime(isoString: string): string {
  const diff = Date.now() - new Date(isoString).getTime();
  const seconds = Math.floor(diff / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.floor(months / 12)}y ago`;
}

export const UserDataTable: React.FC<Props> = ({ data, isLoading = false, sortConfig, onSort, emptyMessage }) => {
  const navigate = useNavigate();

  const dealerIds = useMemo(
    () => Array.from(new Set(data.map((u) => u.dealer_id).filter((id): id is string => Boolean(id)))),
    [data],
  );
  const { hasPermission } = usePermissions();
  const dealersQuery = useDealersByIds(dealerIds, hasPermission({ resource: 'dealer', action: 'browse' }));
  const dealerNameMap = useMemo(() => buildDealerNameMap(dealersQuery.data), [dealersQuery.data]);

  const createSortableHeader = (column: string, label: string) => {
    const isSorted = sortConfig?.sort_by === column;
    const direction = sortConfig?.sort_direction;

    return (
      <Button
        onClick={() => onSort?.(column)}
        className="flex items-center space-x-1 transition-colors"
        variant="clear"
        size="none"
      >
        <span>{label}</span>
        {isSorted && (
          <span className="text-mp-link">
            {direction === 'asc' ? (
              <ChevronDownIcon size={14} />
            ) : (
              <ChevronUpIcon size={14} />
            )}
          </span>
        )}
        {!isSorted && <ChevronsUpDownIcon size={14} className="opacity-50" />}
      </Button>
    );
  };

  if (isLoading) {
    return <LoadingSpinner size="xl" className="py-12" message="Loading users..." />;
  }

  const columns: ColumnDef<AdminUser>[] = [
    {
      accessorKey: 'name',
      header: () => createSortableHeader('name', 'Name'),
      cell: (info) => (
        <span className="capitalize font-semibold text-sm">
          {info.getValue() as string}
        </span>
      ),
      enableSorting: false,
    },
    {
      accessorKey: 'email',
      header: () => createSortableHeader('email', 'Email'),
      cell: (info) => <span className="font-mono text-sm">{info.getValue() as string}</span>,
      enableSorting: false,
    },
    {
      accessorKey: 'dealer_id',
      header: () => 'Home Dealer',
      cell: (info) => (
        <HomeDealerCell
          dealerId={info.getValue() as string | null}
          nameMap={dealerNameMap}
          isLoading={dealersQuery.isLoading}
        />
      ),
      enableSorting: false,
    },
    {
      accessorKey: 'status',
      header: () => createSortableHeader('status', 'Status'),
      cell: (info) => (
        <Badge variant={info.getValue() === 'active' ? 'success' : 'error'}>{info.getValue() as string}</Badge>
      ),
      enableSorting: false,
    },
    {
      accessorKey: 'mfa_enforced',
      header: () => createSortableHeader('mfa_enforced', 'MFA Enforced'),
      cell: (info) => <span className="font-mono text-sm">{info.getValue() as string ? 'Yes' : 'No'}</span>,
      enableSorting: false,
    },
    {
      accessorKey: 'last_sign_in_at',
      header: () => createSortableHeader('last_sign_in_at', 'Last Logged In'),
      cell: (info) => {
        const value = info.getValue() as string | null;
        if (!value) return <span className="font-mono text-sm text-mp-muted">Never</span>;
        const absolute = new Date(value).toLocaleString();
        return (
          <Tooltip content={absolute}>
            <span className="font-mono text-sm">{formatRelativeTime(value)}</span>
          </Tooltip>
        );
      },
      enableSorting: false,
    },
    {
      accessorKey: 'created_at',
      header: () => createSortableHeader('created_at', 'Created'),
      cell: (info) => <span className="font-mono text-sm">{new Date(info.getValue() as string).toLocaleDateString()}</span>,
      enableSorting: false,
    },
    {
      id: 'actions',
      header: () => 'Actions',
      cell: (info) => (
        <div className="flex space-x-2">
          <Tooltip content="View user">
            <TextLink
              onClick={() => navigate(`/admin/users/${info.row.original.id}`)}
              color="primary"
              startIcon={<EyeIcon size={20} />}
            />
          </Tooltip>
          <PermissionGuard permission={{ resource: 'user', action: 'edit' }}>
            <Tooltip content="Edit user">
              <TextLink
                onClick={() => navigate(`/admin/users/${info.row.original.id}/edit`)}
                color="secondary"
                startIcon={<EditIcon size={18} />}
              />
            </Tooltip>
          </PermissionGuard>
        </div>
      ),
      enableSorting: false,
    },
  ];

  return (
    <div className="space-y-6">
      <Table<AdminUser>
        data={data}
        columns={columns}
        enableSorting={false}
        emptyMessage={emptyMessage}
      />
    </div>
  );
};
